// ── ตัวช่วยเรียก analytics API สำหรับหน้ารายงานทั้งสามหน้า ────────────────────
import rawAxios from 'axios'
import axiosIns from '@axios'

/*
 * ⚠️ instance เปล่า ห้ามใช้ @axios ตรง ๆ
 *
 * request interceptor ของ jwtService เขียนทับ Authorization ทุกครั้งที่มี accessToken
 * ใน localStorage แบบไม่มีเงื่อนไข (src/@core/auth/jwt/jwtService.js) และ accessToken
 * ตัวนั้นถูกเซ็นในเบราว์เซอร์โดย @fake-db ด้วย secret คนละตัวกับ JWT_SECRET ของ API
 * -> /analytics/v1/* ตอบ 403 ทุกครั้ง
 */
export const analyticsHttp = rawAxios.create({ baseURL: axiosIns.defaults.baseURL })

/**
 * token ที่ API ยอมรับ — /users/v1/login คืนมาตอนล็อกอินและเก็บติดกับ userData
 * (ไม่ใช่ accessToken ที่ @fake-db เซ็นเอง)
 */
export const apiToken = () => {
  try {
    const raw = localStorage.getItem('userData')

    return (raw ? JSON.parse(raw).token : null) || null
  } catch (error) {
    return null
  }
}

/**
 * ยิงหลาย request พร้อมกันโดยอ้างอิงด้วย "ชื่อ" ไม่ใช่ตำแหน่ง
 *
 * ⚠️ เหตุผลที่ต้องมีตัวนี้ ไม่ใช้ Promise.allSettled + destructure array ตรง ๆ
 *
 * เดิมเขียนแบบ `const [res, utm, src, geo, ...paged] = await Promise.allSettled([...])`
 * ซึ่งผูกกับ "ลำดับ" ของ array พอมีคนแทรก request ใหม่ผิดตำแหน่ง ผลลัพธ์ทั้งชุดเลื่อน
 * แล้วทุกตารางได้ข้อมูลผิดชุด — จำนวนแถวถูกแต่ทุกช่องว่าง เพราะ field ไม่ตรงกับ key
 * (เกิดจริงตอนเพิ่ม pagination แล้ววาง spread ไว้กลาง array)
 *
 * แบบนี้ชื่อกับผลลัพธ์ผูกกันตายตัว จะเพิ่ม/ลบ/สลับ request ตรงไหนก็ไม่พัง
 *
 * @param {Record<string, Promise>} jobs   { ชื่อ: promise }
 * @returns {Record<string, {ok: boolean, data: any, hasMore: boolean, reason: any}>}
 */
export const settleAll = async jobs => {
  const keys = Object.keys(jobs)
  const settled = await Promise.allSettled(keys.map(k => jobs[k]))
  const out = {}

  keys.forEach((key, i) => {
    const r = settled[i]
    const ok = r.status === 'fulfilled'

    out[key] = {
      ok,
      // list report ห่อมาเป็น { data, page, per_page, has_more } ส่วน dashboard เป็น object
      data: ok ? (r.value?.data?.data ?? null) : null,
      hasMore: !!(ok && r.value?.data?.has_more),
      /*
       * total / totalPages มีเฉพาะรายงานที่ API นับให้ (ดู COUNTABLE ใน report.model.js)
       * ที่เหลือได้ null -> ต้องเช็คก่อนใช้ ห้าม assume ว่ามีทุกตัว
       */
      total: ok && Number.isFinite(r.value?.data?.total) ? r.value.data.total : null,
      totalPages: ok && Number.isFinite(r.value?.data?.total_pages) ? r.value.data.total_pages : null,
      cached: !!(ok && r.value?.data?.cached),
      reason: ok ? null : r.reason,
    }
  })

  return out
}

/** ผลของ request เดียวในรูปแบบเดียวกับที่ settleAll คืนต่อหนึ่งคีย์ */
export const settleOne = async promise => {
  const [only] = Object.values(await settleAll({ only: promise }))

  return only
}

/**
 * ยิงหลาย request พร้อมกัน แต่ส่งผลกลับ "ทีละตัวทันทีที่มาถึง" ไม่รอให้ครบ
 *
 * ⚠️ เหตุผลที่ต้องมีตัวนี้ ไม่ใช้ settleAll กับหน้าที่มีรายงานหนักปนเบา
 *
 * settleAll เป็น Promise.allSettled = ได้ผลพร้อมกันตอนตัวที่ช้าที่สุดเสร็จ
 * หน้ารายงานหลักยิง 7 request ที่ใช้เวลาต่างกันมาก (วัดบน prod ตอน cache ว่าง:
 * top_search_keywords 448ms แต่ geo_detail 22,413ms) พอรอให้ครบ ผู้ใช้จึงเห็น
 * spinner ยาว 22 วินาที ทั้งที่ตัวเลขหัวตารางพร้อมแสดงตั้งแต่วินาทีแรก
 *
 * ตัวนี้เรียก onSettle(key, result) ทันทีที่แต่ละตัวเสร็จ แล้วคืน promise ที่
 * resolve เมื่อครบทุกตัว (ไว้ปิด spinner รวม) — ตัวไหนพังก็ไม่ล้มตัวอื่น
 *
 * @param {Record<string, Promise>} jobs
 * @param {(key: string, result: object) => void} onSettle
 */
export const settleStream = (jobs, onSettle) => Promise.all(
  Object.entries(jobs).map(async ([key, promise]) => {
    const result = await settleOne(promise)

    onSettle(key, result)

    return result
  }),
)

/** แถวของ list report — คืน [] เสมอเมื่อพัง เพื่อให้ตารางไม่ต้องเช็ค null */
export const rowsOf = result => (Array.isArray(result?.data) ? result.data : [])

/** ข้อความ error ที่บอกวิธีแก้ ไม่ใช่แค่รหัสสถานะ */
export const loadErrorMessage = error => {
  const status = error?.response?.status

  /*
   * 401 = หมดอายุ/ถูกถอน, 403 = ลายเซ็นไม่ผ่าน (middlewares/auth.middleware.js ฝั่ง API)
   * access token อายุ 2 ชั่วโมงนับจากตอนล็อกอิน (controllers/usr.controller.js -> generateToken)
   * ปกติ jwtService จะรีเฟรชให้เงียบ ๆ แล้ว sync ลง userData.token ให้ด้วย
   * ถ้ายังเจอข้อความนี้แปลว่า refresh token หมดอายุ (7 วัน) หรือ token ถูกถอนสิทธิ์
   */
  if (status === 401 || status === 403) {
    return 'Token หมดอายุหรือใช้ไม่ได้ — ออกจากระบบแล้วเข้าใหม่'
  }

  return `โหลดรายงานไม่สำเร็จ${status ? ` (HTTP ${status})` : ''}`
}

/** ชื่อรายงานที่โหลดไม่ได้ -> ข้อความเตือนแบบไม่ล้มทั้งหน้า */
export const partialErrorMessage = (results, labels) => {
  const missing = Object.entries(labels)
    .filter(([key]) => results[key] && !results[key].ok)
    .map(([, label]) => label)

  return missing.length
    ? `โหลดไม่ได้: ${missing.join(', ')} — API ยังไม่มีรายงานนี้ (ต้อง deploy ฝั่ง API)`
    : ''
}

/**
 * ข้อความบนแถบแบ่งหน้า
 *
 * ใส่ "จาก Y" ให้เฉพาะรายงานที่ API นับ total มาให้ ถ้าไม่มีก็แสดงแค่เลขหน้าเหมือนเดิม
 * (อย่า fallback เป็น 0 หรือ 1 เพราะจะกลายเป็น "หน้า 3 จาก 1" ตอนที่ยังนับไม่ได้)
 */
export const pageLabel = (page, totalPages, total) => {
  if (!Number.isFinite(totalPages)) return `หน้า ${page}`

  const rows = Number.isFinite(total) ? ` (${total.toLocaleString('en-US')} รายการ)` : ''

  return `หน้า ${page} จาก ${totalPages}${rows}`
}

/** ยังมีหน้าถัดไปไหม — ใช้ total_pages ถ้ามี ไม่มีก็ใช้ has_more แบบเดิม */
export const hasNextPage = (page, totalPages, hasMore) => (
  Number.isFinite(totalPages) ? page < totalPages : !!hasMore
)

/**
 * ควรแสดงแถบแบ่งหน้าไหม
 *
 * เดิมซ่อนทั้งแถบเมื่อมีหน้าเดียว ทำให้ตารางที่ข้อมูลไม่ถึงหนึ่งหน้าไม่บอกอะไรเลย
 * ว่ามีทั้งหมดกี่รายการ ตอนนี้ถ้า API นับ total มาให้ ก็แสดงแถบไว้เพื่อบอกจำนวน
 * (ปุ่มก่อนหน้า/ถัดไปจะซ่อนเอง ดู hasMultiplePages)
 */
export const showPager = (page, totalPages, hasMore) => (
  page > 1 || !!hasMore || Number.isFinite(totalPages)
)

/** มีมากกว่า 1 หน้าจริงไหม — ใช้ตัดสินว่าจะโชว์ปุ่มก่อนหน้า/ถัดไป */
export const hasMultiplePages = (page, totalPages, hasMore) => (
  page > 1 || hasNextPage(page, totalPages, hasMore)
)
